import { SkFont } from "@shopify/react-native-skia";
import React from "react";

import PlatformBarChartView, {
  PlatformBarChartData,
} from "./PlatformBarChartView";

/**
 * Props for PlatformBarChart component
 */
export interface PlatformBarChartProps {
  /** Array of data objects to be displayed in the chart */
  data: PlatformBarChartData[];
  /** Key name for x-axis values */
  xKey: string;
  /** Key name for y-axis values */
  yKey: string;
  /** Color for the bars */
  color: string;
  /** Skia font for labels (native only) */
  font?: SkFont | null;
  /** Chart width in pixels (default: 350) */
  width?: number;
  /** Chart height in pixels (default: 250) */
  height?: number;
  /** Padding between domain and chart edge (default: 20) */
  domainPadding?: number;
  /** Padding around the chart (default: 20) */
  padding?: number;
  /** X-axis configuration */
  xAxis?: {
    labelColor?: string;
    tickCount?: number;
  };
  /** Y-axis configuration */
  yAxis?: {
    labelColor?: string;
  }[];
  /** Width of individual bars (default: 25) */
  barWidth?: number;
  /** Whether to show value labels on bars (default: true) */
  showLabels?: boolean;
}

/**
 * Container component for platform-aware bar chart.
 * Provides default values and passes all configuration to the View component.
 * @param root0 - Component props
 * @param root0.data - Array of data objects containing x and y values for bars
 * @param root0.xKey - Property name to use for x-axis values from data objects
 * @param root0.yKey - Property name to use for y-axis values from data objects
 * @param root0.color - Hex or named color string for bar fill color
 * @param root0.font - Skia font object for rendering labels on native platforms
 * @param root0.width - Chart width in pixels (defaults to 350)
 * @param root0.height - Chart height in pixels (defaults to 250)
 * @param root0.domainPadding - Padding in pixels between data domain and chart edges (defaults to 20)
 * @param root0.padding - Padding in pixels around entire chart area (defaults to 20)
 * @param root0.xAxis - Configuration object for x-axis styling and tick formatting
 * @param root0.yAxis - Array of configuration objects for y-axis styling
 * @param root0.barWidth - Width in pixels of individual bar elements (defaults to 25)
 * @param root0.showLabels - Whether to display value labels on top of bars (defaults to true)
 * @remarks
 * This component acts as a thin wrapper to provide sensible defaults
 * while delegating all rendering logic to the View component.
 */
const PlatformBarChartContainer: React.FC<PlatformBarChartProps> = ({
  data,
  xKey,
  yKey,
  color,
  font,
  width = 350,
  height = 250,
  domainPadding = 20,
  padding = 20,
  xAxis,
  yAxis,
  barWidth = 25,
  showLabels = true,
}) => (
  <PlatformBarChartView
    data={data}
    xKey={xKey}
    yKey={yKey}
    color={color}
    font={font}
    width={width}
    height={height}
    domainPadding={domainPadding}
    padding={padding}
    xAxis={xAxis}
    yAxis={yAxis}
    barWidth={barWidth}
    showLabels={showLabels}
  />
);

export default PlatformBarChartContainer;
